import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom"
import { useEffect, useState, useRef, useCallback } from "react"
import { SplashScreen } from '@capacitor/splash-screen'
import { isIPad, isNative } from '@/lib/platform'

// Pre-load cached user data synchronously to prevent flicker on page reload
const preloadCachedUserData = () => {
  try {
    const cachedRole = localStorage.getItem('cachedUserRole')
    const cachedBooking = localStorage.getItem('cachedBookingSystemStatus')
    
    if (cachedRole || cachedBooking) {
      console.log('[App] Pre-loaded cached user data:', {
        hasCachedRole: !!cachedRole,
        hasCachedBooking: !!cachedBooking
      })
    }
  } catch (error) {
    console.error('[App] Error pre-loading cached data:', error)
  }
}

// Run pre-load immediately on app initialization
preloadCachedUserData()

// Eager imports for critical UI components
import { BottomNavWrapper } from "@/components/ui/bottom-nav/BottomNavWrapper"
import { TopNav } from "@/components/ui/navigation/TopNav"
import { DownloadAppDialog } from "@/components/shared/DownloadAppDialog"
import { ShareBookingLinkDialog } from "@/components/shared/ShareBookingLinkDialog"
import { InviteArtistDialog } from "@/components/artists/InviteArtistDialog"
import { VersionCheckDialog, type VersionCheckDialogVariant } from "@/components/shared/VersionCheckDialog"
import { AppErrorBoundary } from "@/components/shared/AppErrorBoundary"
import { ConsentBanner } from '@/components/ui/ConsentBanner'
import { OnboardingGuard } from '@/components/onboarding/OnboardingGuard'

// Eager imports for contexts and hooks
import { ShareBookingLinkProvider, useShareBookingLinkContext } from "@/contexts/ShareBookingLinkContext"
import {
  InviteArtistProvider,
  useInviteArtistDialog,
} from "@/contexts/InviteArtistContext"
import { ArtistProvider } from "@/contexts/ArtistContext"
import { SavesProvider } from "@/contexts/SavesContext"
import { ThemeProvider } from "@/contexts/ThemeContext"
import { ArtistsDataProvider } from "@/contexts/ArtistsDataContext"
import { BookingSystemProvider } from "@/contexts/BookingSystemContext"
import { AppReadyProvider, useAppReady } from "@/contexts/AppReadyContext"
import { FeedCacheProvider } from "@/contexts/FeedCacheContext"
import { ArtistsCacheProvider } from "@/contexts/ArtistsCacheContext"
import { useAnalytics } from "@/hooks/useAnalytics"
import { useLanguageDetection } from "@/hooks/useLanguageDetection"
import { useSessionReplay } from "@/hooks/useSessionReplay"
import { useNotificationSetup } from "@/hooks/useNotificationSetup"
import { useDeepLink } from "@/hooks/useDeepLink"
import { useDeviceInfoLogging } from "@/hooks/useDeviceInfoLogging"
import { useScreenProtection } from "@/hooks/useScreenProtection"
import { useVersionCheck } from "@/hooks/useVersionCheck"
import {
  clearUpdateReloadPending,
  removeUpdateReloadSplashElement,
} from '@/lib/updateReloadSplash'
import { initAuthPersistence } from '@/lib/firebase'
import { MainAppPersistentLayer } from './components/shell/MainAppPersistentLayer'
import { shouldPreserveScrollBetweenPaths } from './lib/mainAppPersistentLayer'
import './lib/i18n'

// Eager-loaded pages — avoids chunk-load jank on mobile navigation.
import ArtistProfile from '@/pages/ArtistPage'
import ChatDetailPage from '@/pages/ChatDetailPage'
import ArtistMatchPage from '@/pages/ArtistMatchPage'
import { DiscoverLegacyRedirect } from '@/pages/DiscoverLegacyRedirect'
import { LegacyCommunityPostRedirect } from '@/pages/LegacyCommunityPostRedirect'
import ProfilePage from '@/pages/ProfilePage'
import CityRequestsPage from './pages/CityRequestsPage'
import FotwFlashDetailPage from '@/pages/FotwFlashDetailPage'
import FlashOfTheWeekPage from '@/pages/FlashOfTheWeekPage'
import FlashDetailPage from '@/pages/FlashDetailPage'
import TattooDetailPage from '@/pages/TattooDetailPage'
import HealedDetailPage from '@/pages/HealedDetailPage'
import ArtistDashboardPage from '@/pages/ArtistDashboardPage'
import SettingsPage from '@/pages/SettingsPage'
import CollectionDetailPage from '@/pages/CollectionDetailPage'
import WaitlistLandingPage from '@/pages/WaitlistLandingPage'
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage'
import CookiePolicyPage from '@/pages/CookiePolicyPage'
import SupportPage from '@/pages/SupportPage'
import GetMoretatsMacdonaldsPage from '@/pages/GetMoretatsMacdonaldsPage'
import CreateArtistPage from '@/pages/CreateArtistPage'
import RateArtistPage from '@/pages/RateArtistPage'
import ArtistOnboardingPage from '@/pages/ArtistOnboardingPage'
import ArtistOnboardingFlowPage from '@/pages/ArtistOnboardingFlowPage'
import ArtistOnboardingSuccessPage from '@/pages/ArtistOnboardingSuccessPage'
import QrRedirectPage from '@/pages/QrRedirectPage'
import LiveChatPage from '@/pages/LiveChatPage'
import GoogleCalendarCallback from '@/pages/auth/google/callback'
import AppRedirectPage from '@/pages/AppRedirectPage'
import DealDetailPage from '@/pages/DealDetailPage'
import RequestGuestArtistPage from '@/pages/RequestGuestArtistPage'
import GlobalGuestArtistsPage from '@/pages/GlobalGuestArtistsPage'
import ArtistWaitlistPage from '@/pages/ArtistWaitlistPage'
import PasswordResetPage from '@/pages/PasswordResetPage'
import OnboardingPage from '@/pages/OnboardingPage'
import CommunityPostComposerPage from '@/pages/CommunityPostComposerPage'
import CommunityPostDetailPage from '@/pages/CommunityPostDetailPage'
import GridTestBench from '@/pages/GridTestBench'
import { BookingSystemDashboard } from '@/components/dashboard/BookingSystemDashboard'
import ArtistOnboardingAdminPage from '@/pages/ArtistOnboardingAdminPage'
import LiveChatAdminPage from '@/pages/LiveChatAdminPage'
import FotwUploadsPage from '@/pages/FotwUploadsPage'
import BoardPostDraftsAdminPage from '@/pages/BoardPostDraftsAdminPage'
import AnalyticsPage from '@/pages/AnalyticsPage'
import CoreAnalyticsPage from '@/pages/CoreAnalyticsPage'
import ArtistBookingPublicPage from '@/pages/ArtistBookingPublicPage'
import DirectBookingPage from '@/pages/DirectBookingPage'

function _pullPagesIntoEntryChunk(..._refs: readonly unknown[]) {}
_pullPagesIntoEntryChunk(ArtistMatchPage, ProfilePage)

function AppContent() {
  const location = useLocation()
  const prevPathRef = useRef<string | null>(null)

  const { trackPageView } = useAnalytics()
  useLanguageDetection()
  const { showDownloadAppDialog, closeDownloadAppDialog, dialogContent } = useNotificationSetup()
  const { contentReady, reachMilestone } = useAppReady()
  
  const [showVersionCheckDialog, setShowVersionCheckDialog] = useState(false)
  const [versionDialogVariant, setVersionDialogVariant] =
    useState<VersionCheckDialogVariant>('default')
  const [errorBoundaryKey, setErrorBoundaryKey] = useState(0)

  const versionDialogVariantRef = useRef(versionDialogVariant)
  versionDialogVariantRef.current = versionDialogVariant

  const openVersionUpdateDialog = useCallback(() => {
    setVersionDialogVariant('default')
    setShowVersionCheckDialog(true)
  }, [])

  const { dialogInfo, loading: versionInfoLoading } = useVersionCheck({
    onUpdateNeeded: openVersionUpdateDialog,
  })

  const handleVersionDialogOpenChange = useCallback((open: boolean) => {
    if (!open) {
      if (versionDialogVariantRef.current === 'stale-chunk') {
        setErrorBoundaryKey((k) => k + 1)
      }
      setVersionDialogVariant('default')
    }
    setShowVersionCheckDialog(open)
  }, [])

  const handleChunkLoadError = useCallback(() => {
    setVersionDialogVariant('stale-chunk')
    setShowVersionCheckDialog(true)
  }, [])

  // Tear down launch-style overlay once the UI is ready again
  useEffect(() => {
    if (!contentReady) return

    const removeOverlay = () => {
      clearUpdateReloadPending()
      removeUpdateReloadSplashElement()
    }

    // Let the app paint underneath before removing the update overlay
    requestAnimationFrame(() => {
      requestAnimationFrame(removeOverlay)
    })
  }, [contentReady])
  
  // Signal app started milestone
  useEffect(() => {
    console.log('[App] AppContent mounted')
    reachMilestone('app_started')
  }, [reachMilestone])

  // iPad: use bottom nav + mobile-style layout at md+ (see index.css `.ipad-bottom-nav-layout`)
  useEffect(() => {
    const root = document.documentElement
    if (isIPad()) root.classList.add('ipad-bottom-nav-layout')
    return () => {
      root.classList.remove('ipad-bottom-nav-layout')
    }
  }, [])
  
  // Initialize session replay
  useSessionReplay()

  
  // Initialize deep link handler for iOS Universal Links
  useDeepLink()
  
  // Initialize device info logging (logs platform, device type, and app version)
  useDeviceInfoLogging()
  
  // Initialize screenshot protection (admins and artists can screenshot)
  useScreenProtection()
  
  // Native splash: keep Capacitor launch screen until first real UI paint (e.g. Feed skeleton)
  useEffect(() => {
    if (!isNative() || !contentReady) return
    SplashScreen.hide({ fadeOutDuration: 0 }).catch(console.error)
  }, [contentReady])
  
  // (PWA first-launch flow removed)

  useEffect(() => {
    const prev = prevPathRef.current
    const next = location.pathname
    if (prev !== null && shouldPreserveScrollBetweenPaths(prev, next)) {
      prevPathRef.current = next
      trackPageView(next)
      return
    }
    window.scrollTo(0, 0)
    prevPathRef.current = next
    trackPageView(next)
  }, [location.pathname, trackPageView])
  

  useEffect(() => {
    const initAuth = async () => {
      reachMilestone('auth_starting')
      await initAuthPersistence()
      console.log('[App] Firebase auth initialized')
      reachMilestone('auth_initialized')
    }
    void initAuth()
  }, [reachMilestone])


  return (
    <>
      <div 
        style={{ 
          minHeight: '100svh'
        }}
        className="bg-background text-foreground">
        {/* Top Navigation - Desktop only */}
        <TopNav />
        
        <OnboardingGuard>
          <MainAppPersistentLayer />
          <AppErrorBoundary
            key={errorBoundaryKey}
            onChunkLoadError={handleChunkLoadError}
          >
            <Routes>
          {/* User Onboarding Route */}
          <Route path="/letsgo" element={<OnboardingPage />} />
          
          <Route path="/" element={<Navigate to="/board" replace />} />
        <Route path="/discover" element={null} />
        <Route path="/board" element={null} />
        <Route path="/board/new" element={<CommunityPostComposerPage />} />
        <Route path="/board/posts/:postId" element={<CommunityPostDetailPage />} />
        <Route path="/pulse" element={<Navigate to="/board" replace />} />
        <Route path="/pulse/new" element={<Navigate to="/board/new" replace />} />
        <Route path="/pulse/posts/:postId" element={<LegacyCommunityPostRedirect />} />
        <Route path="/community" element={<Navigate to="/board" replace />} />
        <Route path="/community/new" element={<Navigate to="/board/new" replace />} />
        <Route path="/community/posts/:postId" element={<LegacyCommunityPostRedirect />} />
        <Route path="/feed" element={<DiscoverLegacyRedirect legacyType="feed" />} />
        <Route path="/test-grid" element={<GridTestBench />} />
        <Route path="/flashes/:flashId" element={<FlashDetailPage />} />
        <Route path="/tattoos/:tattooId" element={<TattooDetailPage />} />
        <Route path="/healed/:healedId" element={<HealedDetailPage />} />
        <Route path="/deals/:identifier" element={<DealDetailPage />} />
        <Route path="/artists" element={<DiscoverLegacyRedirect legacyType="artists" />} />
        <Route path="/artist/:artistId" element={<ArtistProfile />} />
        <Route path="/guest-artists" element={null} />
        <Route path="/guest-artists/request" element={<RequestGuestArtistPage />} />
        <Route path="/guest-artists/global" element={<GlobalGuestArtistsPage />} />
        <Route path="/guest-artists/waitlist" element={<ArtistWaitlistPage />} />
        <Route path="/waitlist/:artistDisplayName" element={<WaitlistLandingPage />} />
        <Route path="/chats" element={null} />
        <Route path="/chats/:chatId" element={<ChatDetailPage />} />
        <Route path="/fotw" element={<FlashOfTheWeekPage />} />
        <Route path="/fotw/:flashId" element={<FotwFlashDetailPage />} />
        <Route path="/artist-dashboard" element={<ArtistDashboardPage />} />
        <Route path="/booking-system/*" element={<BookingSystemDashboard />} />
        <Route path="/tattoo-artist-matcher" element={null} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/macd" element={<GetMoretatsMacdonaldsPage />} />
        
        {/* QR Code Routes */}
        <Route path="/qr1" element={<QrRedirectPage />} />
        <Route path="/qr2" element={<QrRedirectPage />} />
        <Route path="/qr3" element={<QrRedirectPage />} />
        <Route path="/qr/:qrCode" element={<QrRedirectPage />} />
        
        {/* Rate My Artist Routes */}
        <Route path="/rate-my-artist/create" element={<CreateArtistPage />} />
        <Route path="/rate-my-artist/rate/:artistType/:artistId" element={<RateArtistPage />} />
        
        {/* Artist Onboarding Admin Route */}
        <Route path="/onboarding-admin" element={<ArtistOnboardingAdminPage />} />
        
        {/* Artist Onboarding Routes */}
        <Route path="/onboarding" element={<ArtistOnboardingPage />} />
        <Route path="/onboarding/:onboardingId" element={<ArtistOnboardingFlowPage />} />
        <Route path="/onboarding/success" element={<ArtistOnboardingSuccessPage />} />
        
        {/* Live Chat Admin Routes */}
        <Route path="/live-chat-admin" element={<LiveChatAdminPage />} />
        <Route path="/live-chat-admin/:chatId" element={<LiveChatAdminPage />} />
        
        {/* Live Chat Route */}
        <Route path="/live-chat" element={<LiveChatPage />} />
        
        {/* FOTW Uploads Admin Route */}
        <Route path="/fotw-uploads" element={<FotwUploadsPage />} />
        <Route path="/board-post-drafts" element={<BoardPostDraftsAdminPage />} />

        {/* Analytics Admin Routes */}
        <Route path="/analytics" element={<CoreAnalyticsPage />} />
        <Route path="/analytics/detailed" element={<AnalyticsPage />} />

        {/* Google Calendar OAuth Callback */}
        <Route path="/auth/google/callback" element={<GoogleCalendarCallback />} />
        
        {/* Password Reset */}
        <Route path="/reset-password" element={<PasswordResetPage />} />

        {/* Profile Routes */}
        <Route path="/profile" element={null} />
        <Route path="/profile/collection/:collectionId" element={<CollectionDetailPage />} />
        
        {/* Settings Route */}
        <Route path="/settings" element={<SettingsPage />} />
        
        {/* App Redirect Route */}
        <Route path="/app" element={<AppRedirectPage />} />
        
        {/* Direct Booking Route - Uses secure unique link IDs */}
        <Route path="/book/:artistId" element={<DirectBookingPage />} />
        
        {/* For Artists Routes */}
        <Route path="/city-requests" element={<CityRequestsPage />} />
        
        {/* Catch-all route for artist booking usernames - MUST BE LAST */}
        {/* This handles URLs like getmoretats.com/artistName and redirects to chat */}
        <Route path="/:artistName" element={<ArtistBookingPublicPage />} />
      </Routes>
          </AppErrorBoundary>
        </OnboardingGuard>
      
      {/* Bottom Navigation */}
      <BottomNavWrapper />
      
      {/* Download App Dialog */}
      <DownloadAppDialog 
        isOpen={showDownloadAppDialog}
        onOpenChange={closeDownloadAppDialog}
        title={dialogContent.title}
        description={dialogContent.description}
      />

      <VersionCheckDialog
        isOpen={showVersionCheckDialog}
        onOpenChange={handleVersionDialogOpenChange}
        variant={versionDialogVariant}
        versionInfo={versionDialogVariant === 'default' ? dialogInfo : null}
        versionInfoLoading={versionInfoLoading}
      />
      
      {/* Share Booking Link Dialog */}
      <ShareBookingLinkDialogComponent />

      <InviteArtistDialogComponent />
      
      {/* Consent Banner */}
      <ConsentBanner />
      </div>
    </>
  )
}

function ContextsInitializer({ children }: { children: React.ReactNode }) {
  const { reachMilestone } = useAppReady()
  
  useEffect(() => {
    console.log('[App] Contexts initializing')
    reachMilestone('contexts_initializing')
  }, [reachMilestone])
  
  useEffect(() => {
    // Small delay to show the initializing progress before ready
    const timer = setTimeout(() => {
      console.log('[App] All contexts initialized')
      reachMilestone('contexts_ready')
    }, 50)
    return () => clearTimeout(timer)
  }, [reachMilestone])
  
  return <>{children}</>
}

function ShareBookingLinkDialogComponent() {
  const { showShareBookingLinkDialog, closeShareBookingLinkDialog } = useShareBookingLinkContext()

  return (
    <ShareBookingLinkDialog 
      isOpen={showShareBookingLinkDialog}
      onOpenChange={closeShareBookingLinkDialog}
    />
  )
}

function InviteArtistDialogComponent() {
  const { showInviteArtistDialog, closeInviteArtistDialog } = useInviteArtistDialog()

  return (
    <InviteArtistDialog
      open={showInviteArtistDialog}
      onOpenChange={(next) => {
        if (!next) closeInviteArtistDialog()
      }}
    />
  )
}

export default function App() {
  return (
    <Router>
      <AppReadyProvider>
        <ThemeProvider>
          <ArtistsDataProvider>
            <ArtistProvider>
              <SavesProvider>
                <BookingSystemProvider>
                  <InviteArtistProvider>
                    <ShareBookingLinkProvider>
                      <FeedCacheProvider>
                        <ArtistsCacheProvider>
                          <ContextsInitializer>
                            <AppContent />
                          </ContextsInitializer>
                        </ArtistsCacheProvider>
                      </FeedCacheProvider>
                    </ShareBookingLinkProvider>
                  </InviteArtistProvider>
                </BookingSystemProvider>
              </SavesProvider>
            </ArtistProvider>
          </ArtistsDataProvider>
        </ThemeProvider>
      </AppReadyProvider>
    </Router>
  )
}
